import { useEffect, useState } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Line, Bar, Doughnut, Scatter, Chart } from 'react-chartjs-2';
import EnhancedDateSelector from '../components/EnhancedDateSelector';

const AdditionalMetrics = () => {
  const [investments, setInvestments] = useState([]);
  const [monthlyRemittances, setMonthlyRemittances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null, label: 'All Time' });
  const [totalDeposits, setTotalDeposits] = useState(0);
  const initialDeposit = 1000000; // $1M initial deposit

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch investments
      const invResponse = await api.get('/investments');
      setInvestments(invResponse.data);
      
      // Fetch monthly remittances
      const remitResponse = await api.get('/transactions/remittances-by-month');
      setMonthlyRemittances(remitResponse.data.monthly_remittances || []);
      
      // Fetch deposits
      const dashResponse = await api.get('/dashboard');
      setTotalDeposits(dashResponse.data.deposits || 0);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load metrics data');
      setLoading(false);
    }
  };

  // Helper function to parse currency
  const parseCurrency = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      return parseFloat(value.replace(/[$,]/g, '')) || 0;
    }
    return 0;
  };

  // Get available years
  const getAvailableYears = () => {
    const years = new Set();
    investments.forEach(inv => {
      const fundedDate = new Date(inv['Funded Date'] || inv['Created Date']);
      if (!isNaN(fundedDate)) {
        years.add(fundedDate.getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  };

  const availableYears = getAvailableYears();

  // Filter investments by date range
  const filteredInvestments = investments.filter(inv => {
    const fundedDate = new Date(inv['Funded Date'] || inv['Created Date']);
    if (dateRange.startDate && fundedDate < dateRange.startDate) return false;
    if (dateRange.endDate && fundedDate > dateRange.endDate) return false;
    return true;
  });

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      point: {
        radius: 0,
        hoverRadius: 5
      },
      line: {
        tension: 0.4
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 10,
          font: { size: 11 }
        }
      }
    },
    scales: {
      y: {
        grid: {
          display: false
        },
        ticks: {
          callback: function(value) {
            return '$' + (value/1000).toFixed(0) + 'k';
          },
          font: { size: 10 }
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  // 1) CUMULATIVE INVESTMENT vs RETURNS
  const getCumulativeInvestmentReturns = () => {
    const monthlyData = {};
    
    // Group by month
    filteredInvestments.forEach(inv => {
      const date = new Date(inv['Funded Date'] || inv['Created Date']);
      if (isNaN(date)) return;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { invested: 0, returns: 0 };
      }
      monthlyData[monthKey].invested += parseCurrency(inv['Purchase Price']);
    });

    monthlyRemittances.forEach(m => {
      if (!monthlyData[m.month]) {
        monthlyData[m.month] = { invested: 0, returns: 0 };
      }
      monthlyData[m.month].returns += m.amount;
    });

    const sortedMonths = Object.keys(monthlyData).sort();
    let cumInvested = 0, cumReturns = 0;
    
    const labels = sortedMonths.map(m => {
      const [year, month] = m.split('-');
      return `${month}/${year.slice(2)}`;
    });
    
    const investedData = [], returnsData = [];
    sortedMonths.forEach(m => {
      cumInvested += monthlyData[m].invested;
      cumReturns += monthlyData[m].returns;
      investedData.push(cumInvested);
      returnsData.push(cumReturns);
    });

    return {
      labels,
      datasets: [
        {
          label: 'Cumulative Investment',
          data: investedData,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.4,
          borderWidth: 2
        },
        {
          label: 'Cumulative Returns',
          data: returnsData,
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          borderWidth: 2
        }
      ]
    };
  };

  // 2) MONTHLY COLLECTIONS RUN-RATE
  const getMonthlyCollections = () => {
    const labels = monthlyRemittances.map(m => {
      const [year, month] = m.month.split('-');
      return `${month}/${year.slice(2)}`;
    });
    
    return {
      labels,
      datasets: [{
        label: 'Monthly Collections',
        data: monthlyRemittances.map(m => m.amount),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
        borderWidth: 2
      }]
    };
  };

  // 4) UNALLOCATED CASH BALANCE
  const getCashBalance = () => {
    const monthlyData = {};
    
    filteredInvestments.forEach(inv => {
      const date = new Date(inv['Funded Date'] || inv['Created Date']);
      if (isNaN(date)) return;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) monthlyData[monthKey] = { inflow: 0, outflow: 0 };
      monthlyData[monthKey].outflow += parseCurrency(inv['Purchase Price']);
    });

    monthlyRemittances.forEach(m => {
      if (!monthlyData[m.month]) monthlyData[m.month] = { inflow: 0, outflow: 0 };
      monthlyData[m.month].inflow += m.amount;
    });

    const sortedMonths = Object.keys(monthlyData).sort();
    let balance = initialDeposit;
    
    const labels = sortedMonths.map(m => {
      const [year, month] = m.split('-');
      return `${month}/${year.slice(2)}`;
    });
    
    const balanceData = sortedMonths.map(m => {
      balance += (monthlyData[m].inflow - monthlyData[m].outflow);
      return balance;
    });

    return {
      labels,
      datasets: [{
        label: 'Cash Balance',
        data: balanceData,
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        tension: 0.4,
        fill: true,
        borderWidth: 2
      }]
    };
  };

  // 6) DELINQUENCY PIPELINE
  const getDelinquencyPipeline = () => {
    const statusCounts = {};
    const statuses = ['Current', 'Late 31-60 Days', 'Late 61-90 Days', 'Late 91-120 Days', 'Late 121+ Days'];
    
    filteredInvestments.forEach(inv => {
      const status = inv['Status'] || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    return {
      labels: Object.keys(statusCounts),
      datasets: [{
        label: 'Count',
        data: Object.values(statusCounts),
        backgroundColor: [
          'rgba(16, 185, 129, 0.7)',
          'rgba(251, 191, 36, 0.7)',
          'rgba(251, 146, 60, 0.7)',
          'rgba(239, 68, 68, 0.7)',
          'rgba(153, 27, 27, 0.7)'
        ]
      }]
    };
  };

  // 11) LOSS SEVERITY
  const getLossSeverity = () => {
    const severityBuckets = { '0-10%': 0, '10-20%': 0, '20-30%': 0, '30-40%': 0, '40-50%': 0, '50%+': 0 };
    
    filteredInvestments.forEach(inv => {
      const status = inv['Status'] || '';
      if (['Late 61-90 Days', 'Late 91-120 Days', 'Late 121+ Days', 'Settled', 'Write Off'].includes(status)) {
        const rtrPaid = parseFloat(inv['% RTR Paid'] || 0);
        const severity = (1 - rtrPaid) * 100;
        
        if (severity < 10) severityBuckets['0-10%']++;
        else if (severity < 20) severityBuckets['10-20%']++;
        else if (severity < 30) severityBuckets['20-30%']++;
        else if (severity < 40) severityBuckets['30-40%']++;
        else if (severity < 50) severityBuckets['40-50%']++;
        else severityBuckets['50%+']++;
      }
    });

    return {
      labels: Object.keys(severityBuckets),
      datasets: [{
        label: 'Count',
        data: Object.values(severityBuckets),
        backgroundColor: 'rgba(239, 68, 68, 0.7)'
      }]
    };
  };

  // 13) INDUSTRY EXPOSURE
  const getIndustryExposure = () => {
    const industryData = {};
    
    filteredInvestments.forEach(inv => {
      const industry = inv['Industry'] || 'Unknown';
      if (!industryData[industry]) industryData[industry] = 0;
      industryData[industry] += parseCurrency(inv['Net RTR']);
    });

    const sorted = Object.entries(industryData)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    return {
      labels: sorted.map(([industry]) => industry),
      datasets: [{
        label: 'Exposure',
        data: sorted.map(([, amount]) => amount),
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(251, 191, 36, 0.7)',
          'rgba(168, 85, 247, 0.7)',
          'rgba(236, 72, 153, 0.7)',
          'rgba(251, 146, 60, 0.7)',
          'rgba(14, 165, 233, 0.7)',
          'rgba(34, 197, 94, 0.7)',
          'rgba(249, 115, 22, 0.7)',
          'rgba(139, 92, 246, 0.7)'
        ]
      }]
    };
  };

  // 17) FACTOR RATE vs UNIT PROFIT
  const getFactorRateVsProfit = () => {
    const data = filteredInvestments
      .filter(inv => inv['Factor Rate'] && inv['Net Profit'] && inv['Purchase Price'])
      .map(inv => ({
        x: parseFloat(inv['Factor Rate']) || 0,
        y: (parseCurrency(inv['Net Profit']) / Math.max(1, parseCurrency(inv['Purchase Price']))) * 100
      }))
      .slice(0, 100); // Limit to 100 points for performance

    return {
      datasets: [{
        label: 'Investments',
        data,
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 0.8)',
        pointRadius: 4
      }]
    };
  };

  // 19) PAYMENT FREQUENCY MIX
  const getPaymentFrequencyMix = () => {
    const freqData = {};
    
    filteredInvestments.forEach(inv => {
      const freq = inv['Payment Frequency'] || 'Unknown';
      if (!freqData[freq]) freqData[freq] = 0;
      freqData[freq] += parseCurrency(inv['Net RTR']);
    });

    return {
      labels: Object.keys(freqData),
      datasets: [{
        data: Object.values(freqData),
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(251, 191, 36, 0.7)',
          'rgba(168, 85, 247, 0.7)',
          'rgba(236, 72, 153, 0.7)'
        ]
      }]
    };
  };

  // 5) GROSS→NET WATERFALL (simplified - showing key components)
  const getGrossNetWaterfall = () => {
    const grossRTR = filteredInvestments.reduce((sum, inv) => sum + parseCurrency(inv['Gross RTR']), 0);
    const netRTR = filteredInvestments.reduce((sum, inv) => sum + parseCurrency(inv['Net RTR']), 0);
    const purchase = filteredInvestments.reduce((sum, inv) => sum + parseCurrency(inv['Purchase Price']), 0);
    const netProfit = filteredInvestments.reduce((sum, inv) => sum + parseCurrency(inv['Net Profit']), 0);
    const mgmtFees = grossRTR - netRTR;

    return {
      labels: ['Gross RTR', 'Management Fees', 'Net RTR', 'Purchase Price', 'Net Profit'],
      datasets: [{
        label: 'Waterfall',
        data: [grossRTR, -mgmtFees, netRTR, -purchase, netProfit],
        backgroundColor: [
          'rgba(16, 185, 129, 0.7)',
          'rgba(239, 68, 68, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(239, 68, 68, 0.7)',
          'rgba(59, 130, 246, 0.7)'
        ]
      }]
    };
  };

  // 7) VINTAGE PAYDOWN CURVES
  const getVintagePaydownCurves = () => {
    const vintages = {};
    
    filteredInvestments.forEach(inv => {
      const date = new Date(inv['Funded Date'] || inv['Created Date']);
      if (isNaN(date)) return;
      
      const vintage = `${date.getFullYear()}-Q${Math.ceil((date.getMonth() + 1) / 3)}`;
      const rtrPaid = parseFloat(inv['% RTR Paid'] || 0);
      const termPct = parseFloat(inv['Term Percentage'] || 0);
      
      if (!vintages[vintage]) vintages[vintage] = [];
      vintages[vintage].push({ termPct, rtrPaid });
    });

    // Take top 5 vintages
    const topVintages = Object.entries(vintages)
      .sort((a, b) => b[1].length - a[1].length)
      .slice(0, 5);

    const colors = [
      'rgb(59, 130, 246)',
      'rgb(16, 185, 129)',
      'rgb(251, 191, 36)',
      'rgb(168, 85, 247)',
      'rgb(236, 72, 153)'
    ];

    return {
      labels: Array.from({length: 11}, (_, i) => i * 10 + '%'), // 0%, 10%, 20%... 100%
      datasets: topVintages.map(([vintage, data], idx) => ({
        label: vintage,
        data: Array.from({length: 11}, (_, i) => {
          const bucket = i * 10;
          const matches = data.filter(d => Math.floor(d.termPct) >= bucket && Math.floor(d.termPct) < bucket + 10);
          return matches.length > 0 ? matches.reduce((sum, d) => sum + d.rtrPaid, 0) / matches.length : null;
        }),
        borderColor: colors[idx],
        backgroundColor: colors[idx].replace('rgb', 'rgba').replace(')', ', 0.1)'),
        tension: 0.4,
        borderWidth: 2
      }))
    };
  };

  // 8) TIME-TO-80% COLLECTED (Box & Whisker approximation using bar chart)
  const getTimeTo80Collected = () => {
    const vintageTime = {};
    
    filteredInvestments.forEach(inv => {
      const date = new Date(inv['Funded Date'] || inv['Created Date']);
      if (isNaN(date)) return;
      
      const vintage = `${date.getFullYear()}-Q${Math.ceil((date.getMonth() + 1) / 3)}`;
      const rtrPaid = parseFloat(inv['% RTR Paid'] || 0);
      const termMonths = parseFloat(inv['Term (mo)'] || 0);
      const termPct = parseFloat(inv['Term Percentage'] || 0);
      
      // Estimate months to 80% (simplified)
      const monthsTo80 = rtrPaid >= 0.8 ? termMonths * (termPct / 100) : termMonths;
      
      if (!vintageTime[vintage]) vintageTime[vintage] = [];
      if (monthsTo80 > 0) vintageTime[vintage].push(monthsTo80);
    });

    const labels = Object.keys(vintageTime).sort().slice(-8);
    const avgTimes = labels.map(v => {
      const times = vintageTime[v];
      return times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    });

    return {
      labels,
      datasets: [{
        label: 'Avg Months to 80%',
        data: avgTimes,
        backgroundColor: 'rgba(59, 130, 246, 0.7)'
      }]
    };
  };

  // 10) ROLL-RATE MATRIX (simplified heatmap visualization)
  const getRollRateMatrix = () => {
    const statuses = ['Current', 'Late 31-60', 'Late 61-90', 'Late 91-120', 'Late 121+'];
    const matrix = {};
    
    statuses.forEach(from => {
      matrix[from] = {};
      statuses.forEach(to => {
        matrix[from][to] = Math.random() * 20; // Placeholder data
      });
    });

    return {
      labels: statuses,
      datasets: statuses.map((from, idx) => ({
        label: from,
        data: statuses.map(to => matrix[from][to]),
        backgroundColor: `rgba(${59 + idx * 40}, ${130 - idx * 20}, ${246 - idx * 40}, 0.7)`
      }))
    };
  };

  // 12) PAYMENT LAG PROFILE (histogram)
  const getPaymentLagProfile = () => {
    // Simulated lag buckets (would need actual transaction data with scheduled vs cleared dates)
    const lagBuckets = {
      '0-7 days': Math.floor(Math.random() * 5000),
      '8-14 days': Math.floor(Math.random() * 3000),
      '15-30 days': Math.floor(Math.random() * 2000),
      '31-60 days': Math.floor(Math.random() * 1000),
      '61-90 days': Math.floor(Math.random() * 500),
      '90+ days': Math.floor(Math.random() * 300)
    };

    return {
      labels: Object.keys(lagBuckets),
      datasets: [{
        label: 'Transaction Count',
        data: Object.values(lagBuckets),
        backgroundColor: [
          'rgba(16, 185, 129, 0.7)',
          'rgba(34, 197, 94, 0.7)',
          'rgba(251, 191, 36, 0.7)',
          'rgba(251, 146, 60, 0.7)',
          'rgba(239, 68, 68, 0.7)',
          'rgba(153, 27, 27, 0.7)'
        ]
      }]
    };
  };

  // 14) CRITERIA SET OVER TIME
  const getCriteriaSetOverTime = () => {
    const monthlyData = {};
    
    filteredInvestments.forEach(inv => {
      const date = new Date(inv['Funded Date'] || inv['Created Date']);
      if (isNaN(date)) return;
      
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const criteriaSet = inv['Criteria Set'] || 'Unknown';
      
      if (!monthlyData[monthKey]) monthlyData[monthKey] = {};
      if (!monthlyData[monthKey][criteriaSet]) monthlyData[monthKey][criteriaSet] = 0;
      monthlyData[monthKey][criteriaSet] += parseCurrency(inv['Purchase Price']);
    });

    const sortedMonths = Object.keys(monthlyData).sort().slice(-24); // Last 24 months
    const criteriaSets = [...new Set(filteredInvestments.map(i => i['Criteria Set'] || 'Unknown'))].slice(0, 5);
    
    const labels = sortedMonths.map(m => {
      const [year, month] = m.split('-');
      return `${month}/${year.slice(2)}`;
    });

    const colors = [
      'rgba(59, 130, 246, 0.7)',
      'rgba(16, 185, 129, 0.7)',
      'rgba(251, 191, 36, 0.7)',
      'rgba(168, 85, 247, 0.7)',
      'rgba(236, 72, 153, 0.7)'
    ];

    return {
      labels,
      datasets: criteriaSets.map((cs, idx) => ({
        label: cs,
        data: sortedMonths.map(m => monthlyData[m]?.[cs] || 0),
        backgroundColor: colors[idx],
        borderColor: colors[idx].replace('0.7', '1'),
        borderWidth: 1
      }))
    };
  };

  // 15) FICO MIX OVER TIME
  const getFicoMixOverTime = () => {
    const monthlyData = {};
    
    filteredInvestments.forEach(inv => {
      const date = new Date(inv['Funded Date'] || inv['Created Date']);
      if (isNaN(date)) return;
      
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const fico = inv['FICO Range'] || 'Unknown';
      
      if (!monthlyData[monthKey]) monthlyData[monthKey] = {};
      if (!monthlyData[monthKey][fico]) monthlyData[monthKey][fico] = 0;
      monthlyData[monthKey][fico] += parseCurrency(inv['Purchase Price']);
    });

    const sortedMonths = Object.keys(monthlyData).sort().slice(-24);
    const ficoRanges = [...new Set(filteredInvestments.map(i => i['FICO Range'] || 'Unknown'))].slice(0, 5);
    
    const labels = sortedMonths.map(m => {
      const [year, month] = m.split('-');
      return `${month}/${year.slice(2)}`;
    });

    const colors = [
      'rgba(16, 185, 129, 0.7)',
      'rgba(34, 197, 94, 0.7)',
      'rgba(251, 191, 36, 0.7)',
      'rgba(251, 146, 60, 0.7)',
      'rgba(239, 68, 68, 0.7)'
    ];

    return {
      labels,
      datasets: ficoRanges.map((fico, idx) => ({
        label: fico,
        data: sortedMonths.map(m => monthlyData[m]?.[fico] || 0),
        backgroundColor: colors[idx],
        fill: true,
        tension: 0.4
      }))
    };
  };

  // 16) PARTICIPATION % vs DEAL SIZE
  const getParticipationVsDealSize = () => {
    const data = filteredInvestments
      .map(inv => {
        const purchase = parseCurrency(inv['Purchase Price']);
        const payback = parseCurrency(inv['Payback Amount']) || parseCurrency(inv['Gross RTR']) || parseCurrency(inv['Net RTR']);
        const dealSize = payback > 0 ? payback : purchase * 1.3; // Estimate if missing
        const participation = dealSize > 0 ? (purchase / dealSize) * 100 : 0;
        
        return {
          x: dealSize,
          y: Math.min(participation, 100),
          r: Math.max(5, Math.min(15, Math.sqrt(purchase) / 50)) // Bubble size between 5-15
        };
      })
      .filter(d => d.x > 0 && d.y > 0)
      .slice(0, 150);

    return {
      datasets: [{
        label: 'Investments',
        data,
        backgroundColor: 'rgba(59, 130, 246, 0.6)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1
      }]
    };
  };

  // 18) TERM REALIZATION
  const getTermRealization = () => {
    const freqData = {};
    
    filteredInvestments.forEach(inv => {
      const freq = inv['Payment Frequency'] || 'Unknown';
      const termPct = parseFloat(inv['Term Percentage'] || 0);
      
      if (!freqData[freq]) freqData[freq] = [];
      if (termPct > 0) freqData[freq].push(termPct);
    });

    const labels = Object.keys(freqData);
    const avgTerms = labels.map(f => {
      const terms = freqData[f];
      return terms.length > 0 ? terms.reduce((a, b) => a + b, 0) / terms.length : 0;
    });

    return {
      labels,
      datasets: [{
        label: 'Avg Term Realization %',
        data: avgTerms,
        backgroundColor: avgTerms.map(t => 
          t < 100 ? 'rgba(16, 185, 129, 0.7)' : 
          t > 100 ? 'rgba(239, 68, 68, 0.7)' : 
          'rgba(251, 191, 36, 0.7)'
        )
      }]
    };
  };
  const getTopExposures = () => {
    const exposures = filteredInvestments
      .map(inv => ({
        id: inv['Investment ID'],
        exposure: parseCurrency(inv['Purchase Price']),
        industry: inv['Industry'],
        status: inv['Status']
      }))
      .sort((a, b) => b.exposure - a.exposure)
      .slice(0, 15);

    return {
      labels: exposures.map(e => e.id),
      datasets: [{
        label: 'Exposure',
        data: exposures.map(e => e.exposure),
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1
      }]
    };
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        {/* Loading Indicator with Spinner */}
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-20 w-20 border-b-4 border-blue-600 mb-4"></div>
            <h3 className="text-2xl font-semibold text-slate-700 mb-2">Loading Additional Metrics...</h3>
            <p className="text-slate-500">Compiling advanced analytics and charts</p>
          </div>
        </div>
        
        {/* Skeleton Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
              <div className="h-6 bg-slate-200 rounded w-48 mb-4"></div>
              <div className="h-64 bg-slate-100 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Additional Metrics</h1>
          <p className="text-slate-600 mt-1">Advanced portfolio analytics and performance metrics</p>
        </div>
      </div>

      {/* Date Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <EnhancedDateSelector
          onDateRangeChange={handleDateRangeChange}
          availableYears={availableYears}
        />
      </div>

      {/* Metrics Grid - First 2 charts in 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* 1) DELINQUENCY PIPELINE */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">1. Delinquency Pipeline</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Distribution of investments by status">ⓘ</span>
          </div>
          <div style={{ height: '300px' }}>
            <Bar data={getDelinquencyPipeline()} options={{
              ...chartOptions,
              scales: {
                y: {
                  ticks: {
                    callback: function(value) {
                      return value;
                    }
                  }
                }
              }
            }} />
          </div>
        </div>

        {/* 2) INDUSTRY EXPOSURE */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">2. Industry Exposure (Top 10)</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Exposure by industry sector">ⓘ</span>
          </div>
          <div style={{ height: '300px' }}>
            <Bar data={getIndustryExposure()} options={chartOptions} />
          </div>
        </div>

        {/* 3) PAYMENT FREQUENCY MIX */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">3. Payment Frequency Mix</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Distribution of exposure by payment frequency">ⓘ</span>
          </div>
          <div style={{ height: '300px' }}>
            <Doughnut data={getPaymentFrequencyMix()} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'right' }
              }
            }} />
          </div>
        </div>

        {/* 4) TOP EXPOSURES */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">4. Top 15 Exposures</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Largest investments by purchase price">ⓘ</span>
          </div>
          <div style={{ height: '400px' }}>
            <Bar data={getTopExposures()} options={{
              indexAxis: 'y',
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false }
              },
              scales: {
                x: {
                  ticks: {
                    callback: function(value) {
                      return '$' + (value/1000).toFixed(0) + 'k';
                    }
                  }
                }
              }
            }} />
          </div>
        </div>

        {/* 8) TIME-TO-80% COLLECTED */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">6. Time-to-80% Collected</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Average months to reach 80% collection by vintage">ⓘ</span>
          </div>
          <div style={{ height: '300px' }}>
            <Bar data={getTimeTo80Collected()} options={{
              ...chartOptions,
              scales: {
                y: {
                  ticks: {
                    callback: function(value) {
                      return value.toFixed(1) + ' mo';
                    }
                  }
                }
              }
            }} />
          </div>
        </div>

        {/* 10) ROLL-RATE MATRIX */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">7. Roll-Rate Matrix</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Status transition patterns (simulated data)">ⓘ</span>
          </div>
          <div style={{ height: '300px' }}>
            <Bar data={getRollRateMatrix()} options={{
              ...chartOptions,
              scales: {
                y: {
                  stacked: true,
                  ticks: {
                    callback: function(value) {
                      return value.toFixed(0) + '%';
                    }
                  }
                },
                x: {
                  stacked: true
                }
              }
            }} />
          </div>
        </div>

        {/* 12) PAYMENT LAG PROFILE */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">8. Payment Lag Profile</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Distribution of payment delays (simulated data)">ⓘ</span>
          </div>
          <div style={{ height: '300px' }}>
            <Bar data={getPaymentLagProfile()} options={{
              ...chartOptions,
              scales: {
                y: {
                  ticks: {
                    callback: function(value) {
                      return value.toLocaleString();
                    }
                  }
                }
              }
            }} />
          </div>
        </div>

        {/* 14) CRITERIA SET OVER TIME */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">9. Criteria Set Over Time</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Investment volume by criteria set over time">ⓘ</span>
          </div>
          <div style={{ height: '300px' }}>
            <Bar data={getCriteriaSetOverTime()} options={{
              ...chartOptions,
              scales: {
                y: {
                  stacked: true,
                  ticks: {
                    callback: function(value) {
                      return '$' + (value/1000).toFixed(0) + 'k';
                    }
                  }
                },
                x: {
                  stacked: true
                }
              }
            }} />
          </div>
        </div>

        {/* 15) FICO MIX OVER TIME */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">10. FICO Mix Over Time</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Investment volume by FICO range over time">ⓘ</span>
          </div>
          <div style={{ height: '300px' }}>
            <Line data={getFicoMixOverTime()} options={chartOptions} />
          </div>
        </div>

      </div>

      {/* Last 2 charts in their own row - larger format */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* 16) PARTICIPATION % vs DEAL SIZE - moved here */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">11. Participation % vs Deal Size</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Your participation percentage relative to total deal size">ⓘ</span>
          </div>
          <div style={{ height: '350px' }}>
            <Scatter data={getParticipationVsDealSize()} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'bottom' }
              },
              scales: {
                x: {
                  title: { display: true, text: 'Deal Size ($)' },
                  ticks: {
                    callback: function(value) {
                      return '$' + (value/1000).toFixed(0) + 'k';
                    }
                  }
                },
                y: {
                  title: { display: true, text: 'Participation %' },
                  max: 100,
                  ticks: {
                    callback: function(value) {
                      return value.toFixed(0) + '%';
                    }
                  }
                }
              }
            }} />
          </div>
        </div>

        {/* 18) TERM REALIZATION - moved here */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">12. Term Realization by Payment Frequency</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Average term % completed by payment frequency (green=early payoff, red=overrun)">ⓘ</span>
          </div>
          <div style={{ height: '350px' }}>
            <Bar data={getTermRealization()} options={{
              ...chartOptions,
              scales: {
                y: {
                  ticks: {
                    callback: function(value) {
                      return value.toFixed(0) + '%';
                    }
                  }
                }
              }
            }} />
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdditionalMetrics;
